/**
 * Firewall entity — destructible grid barrier (V2).
 * Blocks enemy pathfinding like towers. Has HP and reflect.
 * See D-029: Network-security terminology.
 */

import { COLORS } from "../core/constants.ts";
import { DEFENSE } from "../core/palette.ts";

export interface FirewallConfig {
  cost: number;
  color: string;
  colorLight: string;
  hp?: number;
  maxHp?: number;
}

export const FIREWALL_DEFAULTS: FirewallConfig = {
  cost: 40,
  color: COLORS.SENTINEL_GREEN,
  colorLight: DEFENSE.firewall.colorLight,
  hp: 200,
  maxHp: 200,
};

/** GA-17 / D-322 (AC-5, Task-007) — the in-run firewall placement cap
 *  (`RuntimeState.maxFirewalls`). Byte-safe fallback; overridden in place by
 *  {@link applyFirewallData} when `firewalls.json` ships a root `maxFirewalls`
 *  field. `main.ts`/`resetForNewRun.ts` read this live binding instead of
 *  the literal `3`. */
export let FIREWALL_CAP = 3;

/** CR-18: Overwrite defaults from data file at load time */
export function applyFirewallData(data: {
  default: {
    cost: number;
    hp: number;
    maxHp: number;
    color: string;
    colorLight: string;
  };
  maxFirewalls?: number;
}): void {
  FIREWALL_DEFAULTS.cost = data.default.cost;
  FIREWALL_DEFAULTS.hp = data.default.hp;
  FIREWALL_DEFAULTS.maxHp = data.default.maxHp;
  FIREWALL_DEFAULTS.color = data.default.color;
  FIREWALL_DEFAULTS.colorLight = data.default.colorLight;
  if (data.maxFirewalls !== undefined) FIREWALL_CAP = data.maxFirewalls;
}

export class Firewall {
  col = 0;
  row = 0;
  x = 0;
  y = 0;
  cost = 0;
  color: string = COLORS.SENTINEL_GREEN;
  colorLight: string = DEFENSE.firewall.colorLight;
  hp = 200;
  maxHp = 200;
  /** Whether this firewall has the REINFORCED synergy bonus */
  reinforced = false;
  readonly id: string;

  private static nextId = 0;

  constructor() {
    this.id = `fw-${Firewall.nextId++}`;
  }

  init(
    col: number,
    row: number,
    x: number,
    y: number,
    config: FirewallConfig,
  ): this {
    this.col = col;
    this.row = row;
    this.x = x;
    this.y = y;
    this.cost = config.cost;
    this.color = config.color;
    this.colorLight = config.colorLight;
    this.hp = config.hp ?? 200;
    this.maxHp = config.maxHp ?? 200;
    this.reinforced = false;
    return this;
  }

  takeDamage(amount: number): number {
    this.hp = Math.max(0, this.hp - amount);
    // CR-23: Reflect 10% damage. GA-16 (Task-011): this literal is the sole,
    // intentional source of truth for the reflect fraction — the formerly-dead
    // `firewalls.json` `default.reflectPct` data field (never read, always 0.1)
    // was removed rather than wired, a behavior-neutral dead-data cleanup.
    return Math.floor(amount * 0.1);
  }

  get alive(): boolean {
    return this.hp > 0;
  }

  /** Reset static counter (for tests). */
  static resetIdCounter(): void {
    Firewall.nextId = 0;
  }
}
